import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  isFavorite, 
  addToFavorites, 
  removeFromFavorites,
  isInWatchlist,
  addToWatchlist,
  removeFromWatchlist,
  isWatched,
  markAsWatched,
  removeFromWatched,
  getUserRating,
  setUserRating,
  getCollections,
  addMovieToCollection
} from '../lib/storage';

const CardMovie = ({ mov, onUpdate, showActions = true }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watched, setWatched] = useState(false);
  const [userRating, setUserRatingState] = useState(null);
  const [showActionsState, setShowActionsState] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collections, setCollections] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (mov) {
      setIsFav(isFavorite(mov.id));
      setInWatchlist(isInWatchlist(mov.id));
      setWatched(isWatched(mov.id));
      setUserRatingState(getUserRating(mov.id));
      setCollections(getCollections());
    }
  }, [mov]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const handleFavoriteToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUpdating(true);
    
    try {
      if (isFav) {
        removeFromFavorites(mov.id);
        setIsFav(false);
      } else {
        addToFavorites(mov);
        setIsFav(true);
      }
      onUpdate?.();
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleWatchlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUpdating(true);
    
    try {
      if (inWatchlist) {
        removeFromWatchlist(mov.id);
        setInWatchlist(false);
      } else {
        addToWatchlist(mov);
        setInWatchlist(true);
      }
      onUpdate?.();
    } catch (error) {
      console.error('Error toggling watchlist:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleWatchedToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsUpdating(true);
    
    try {
      if (watched) {
        removeFromWatched(mov.id);
        setWatched(false);
        setUserRatingState(null);
      } else {
        markAsWatched(mov);
        setWatched(true);
        // Remove from watchlist if it exists there
        if (inWatchlist) {
          removeFromWatchlist(mov.id);
          setInWatchlist(false);
        }
      }
      onUpdate?.();
    } catch (error) {
      console.error('Error toggling watched:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRating = async (rating) => {
    setIsUpdating(true);
    
    try {
      if (rating === null) {
        // Remove rating
        setUserRating(mov.id, null);
        setUserRatingState(null);
      } else {
        setUserRating(mov.id, rating);
        setUserRatingState(rating);
        
        // Mark as watched if not already
        if (!watched) {
          markAsWatched(mov, rating);
          setWatched(true);
          if (inWatchlist) {
            removeFromWatchlist(mov.id);
            setInWatchlist(false);
          }
        }
      }
      
      setShowRatingModal(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error setting rating:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddToCollection = (collectionId) => {
    try {
      addMovieToCollection(collectionId, mov);
      setShowCollectionModal(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error adding to collection:', error);
    }
  };

  if (!mov) return null;

  const year = mov.release_date?.split('-')[0];
  const posterUrl = mov.poster_path 
    ? `https://image.tmdb.org/t/p/w500${mov.poster_path}` 
    : null;

  const BigText = (string,max) => {
    if (string.length > max) {
      return string.slice(0,max) + ('...');
    }
    else {
      return string;
    }
  }

  return (
    <>
      <div 
        className="group relative bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-[1.02] border border-gray-100 dark:border-gray-700"
        onMouseEnter={() => setShowActionsState(true)}
        onMouseLeave={() => setShowActionsState(false)}
      >
        {/* Quick Action Buttons (Top Right) */}
        {showActions && showActionsState && (
          <div className="absolute top-10 right-2 z-20 flex flex-col space-y-1">
            {/* Favorite Button */}
            <button
              onClick={handleFavoriteToggle}
              disabled={isUpdating}
              className={`p-2 rounded-full backdrop-blur-sm border transition-all duration-200 ${
                isFav
                  ? 'bg-red-500/90 border-red-400 text-white shadow-lg scale-105'
                  : 'bg-black/30 border-white/20 text-white hover:bg-red-500/70 hover:border-red-400'
              } ${isUpdating ? 'opacity-50' : ''}`}
              title={isFav ? 'Remove from favorites' : 'Add to favorites'}
            >
              <svg className={`w-4 h-4 ${isFav ? 'fill-current' : ''}`} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            </button>

            {/* Watchlist Button */}
            <button
              onClick={handleWatchlistToggle}
              disabled={isUpdating || watched}
              className={`p-2 rounded-full backdrop-blur-sm border transition-all duration-200 ${
                inWatchlist
                  ? 'bg-blue-500/90 border-blue-400 text-white shadow-lg scale-105'
                  : 'bg-black/30 border-white/20 text-white hover:bg-blue-500/70 hover:border-blue-400'
              } ${isUpdating || watched ? 'opacity-50' : ''}`}
              title={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
            </button>

            {/* Add to Collection Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowCollectionModal(true);
              }}
              className="p-2 rounded-full backdrop-blur-sm border bg-black/30 border-white/20 text-white hover:bg-purple-500/70 hover:border-purple-400 transition-all duration-200"
              title="Add to collection"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </button>
          </div>
        )}

        {/* Status Indicators (Top Left) */}
        <div className="absolute top-2 left-2 z-20 flex flex-col space-y-1">
          {/* User Rating */}
          {userRating && (
            <div className="flex items-center space-x-1 bg-yellow-500/90 backdrop-blur-sm px-2 py-1 rounded-full border border-yellow-400/50">
              <svg className="w-3 h-3 text-white fill-current" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              <span className="text-white text-xs font-semibold">{userRating}</span>
            </div>
          )}

          {/* Watched Badge */}
          {watched && !userRating && (
            <div className="bg-green-500/90 backdrop-blur-sm px-2 py-1 rounded-full border border-green-400/50">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
          )}

          {/* Watchlist Badge */}
          {inWatchlist && !watched && (
            <div className="bg-blue-500/90 backdrop-blur-sm px-2 py-1 rounded-full border border-blue-400/50">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
          )}
        </div>

        <Link to={`/movie/${mov.id}`} className="block">
          {/* Movie Poster */}
          <div className="relative aspect-[2/3] overflow-hidden bg-gray-200 dark:bg-gray-700">
            
            {/* Loading State */}
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-pulse">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              </div>
            )}

            {/* Image Error State */}
            {imageError && (
              <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
                <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                <span className="text-sm font-medium">No Image</span>
              </div>
            )}

            {/* Movie Poster Image */}
            {posterUrl && !imageError && (
              <img
                src={posterUrl}
                alt={mov.title}
                onLoad={handleImageLoad}
                onError={handleImageError}
                className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              />
            )}
            
            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              
              {/* TMDB Rating Badge */}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="flex items-center space-x-1 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-full border border-white/20">
                  <svg className="w-3 h-3 text-yellow-400 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <span className="text-white text-xs font-semibold">
                    {mov.vote_average?.toFixed(1)}
                  </span>
                </div>
              </div>

              {/* Play Button */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30 hover:bg-white/30 transition-colors duration-200 shadow-lg">
                  <svg className="w-7 h-7 text-white fill-current ml-1" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>

              {/* Bottom Info */}
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <h3 className="font-semibold text-sm mb-2 line-clamp-2 drop-shadow-sm">
                  {mov.title}
                </h3>
                <div className="flex items-center justify-between text-xs opacity-90 mb-2">
                  <div className="flex items-center space-x-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                    </svg>
                    <span>{year || 'N/A'}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-4h3v4h2v-7.5c0-.83-.67-1.5-1.5-1.5S6 9.67 6 10.5V11H4c-.83 0-1.5.67-1.5 1.5V18H4z"/>
                    </svg>
                    <span>{mov.vote_count?.toLocaleString() || '0'}</span>
                  </div>
                </div>

                {/* Quick Actions Bar */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowRatingModal(true);
                    }}
                    className="flex-1 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-xs py-2 px-3 rounded-lg hover:bg-white/30 transition-colors duration-200"
                  >
                    {userRating ? `★ ${userRating}` : 'Rate'}
                  </button>
                  <button
                    onClick={handleWatchedToggle}
                    className={`p-2 rounded-lg transition-colors duration-200 ${
                      watched 
                        ? 'bg-green-500/70 text-white' 
                        : 'bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Card Content */}
          <div className="p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                {BigText(mov.title,20)}
            </h3>
            
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
              <div className="flex items-center space-x-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                </svg>
                <span>{year || 'N/A'}</span>
              </div>
              
              <div className="flex items-center space-x-1">
                <svg className="w-3 h-3 text-yellow-400 fill-current" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <span className="font-medium">{mov.vote_average?.toFixed(1) || 'N/A'}</span>
              </div>
            </div>

            {/* User Status Indicators */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {isFav && (
                  <svg className="w-3 h-3 text-red-500 fill-current" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                )}
                {inWatchlist && (
                  <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                  </svg>
                )}
                {watched && (
                  <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                )}
              </div>
              
              {userRating && (
                <div className="flex items-center space-x-1">
                  <svg className="w-3 h-3 text-yellow-400 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <span className="text-xs font-medium text-gray-900 dark:text-white">{userRating}</span>
                </div>
              )}
            </div>

            {/* Genre hint */}
            {mov.overview && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {mov.overview}
              </p>
            )}
          </div>
        </Link>
      </div>

      {/* Rating Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowRatingModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Rate "{mov.title}"</h3>
            
            <div className="flex justify-center space-x-2 mb-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                <button
                  key={rating}
                  onClick={() => handleRating(rating)}
                  className={`w-10 h-10 rounded-lg border-2 transition-all duration-200 flex items-center justify-center font-semibold ${
                    userRating === rating
                      ? 'bg-yellow-400 border-yellow-500 text-white shadow-lg scale-105'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 hover:border-yellow-400'
                  }`}
                  disabled={isUpdating}
                >
                  {rating}
                </button>
              ))}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowRatingModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                Cancel
              </button>
              {userRating && (
                <button
                  onClick={() => handleRating(null)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
                >
                  Remove Rating
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Collection Modal */}
      {showCollectionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCollectionModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add to Collection</h3>
            
            {collections.length === 0 ? (
              <div className="text-center py-8">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400 mb-4">No collections found</p>
                <Link 
                  to="/collections"
                  className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  onClick={() => setShowCollectionModal(false)}
                >
                  Create Collection
                </Link>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {collections.map((collection) => (
                  <button
                    key={collection.id}
                    onClick={() => handleAddToCollection(collection.id)}
                    className="w-full p-3 text-left bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">{collection.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{collection.movies.length} movies</div>
                  </button>
                ))}
              </div>
            )}
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowCollectionModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for line-clamp */}
      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default CardMovie;